import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../firebase/config";
import { NotificationManager } from "react-notifications";

const SubheadsManager = ({ profileData }) => {
  const [subheads, setSubheads] = useState([]);
  const [form, setForm] = useState({
    head: "",
    headId: "",
    govtPercentage: 0,
    agentPercentage: 0,
    agencyPercentage: 0,
    baseAmount: 0,
    amount: 0,
    subhead: "",
    subheadCode: "",
    posDisplay: "",
    type: "Non_Tax",
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const fetchSubheads = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/subheads`);
      if (res.data?.record?.data) setSubheads(res.data.record.data);
    } catch (err) {
      console.error("Failed to fetch subheads:", err);
    }
  };

  useEffect(() => {
    fetchSubheads();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const now = new Date();
    const newSubhead = {
      id: editingId || Date.now().toString(),
      agency: profileData?.agency || '',
      agencyId: profileData?.agency_id,
      agent: "",
      agentId: "",
      agentPercentage: Number(form.agentPercentage) || 0,
      agentSubhead: false,
      agencyPercentage: Number(form.agencyPercentage) || 0,
      amount: Number(form.amount) || 0,
      archived: false,
      baseAmount: Number(form.baseAmount) || 0,
      changeStamp: 1,
      changeTime: now.toISOString(),
      dutiable: false,
      entryDate: now.toISOString().split("T")[0],
      govtPercentage: Number(form.govtPercentage) || 0,
      head: form.head,
      headId: form.headId,
      oid: profileData?.organisation_id,
      oname: profileData?.organisation,
      parent: "",
      parentId: "",
      posDisplay: form.posDisplay,
      subhead: form.subhead,
      subheadCode: form.subheadCode,
      type: form.type,
      uid: profileData?.email,
      uname: profileData?.name,
      aggregate: false,
    };

    try {
      if (editingId) {
        const res = await axios.post(
          `${API_BASE_URL}/subheads/update/${editingId}`,
          newSubhead
        );
        if (!res.data?.status) {
          NotificationManager.success(
            res.data.message || "Subhead updated successfully",
            "Success"
          );
        } else {
          NotificationManager.error(
            res.data.message || "Failed to update subhead",
            "Error"
          );
        }
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/subheads/create`,
          newSubhead
        );
        if (!res.data?.status) {
          NotificationManager.success(
            res.data.message || "Subhead created successfully",
            "Success"
          );
        } else {
          NotificationManager.error(
            res.data.message || "Failed to create subhead",
            "Error"
          );
        }
      }
      await fetchSubheads();
      setForm({
        head: "",
        headId: "",
        govtPercentage: 0,
        agentPercentage: 0,
        agencyPercentage: 0,
        baseAmount: 0,
        amount: 0,
        subhead: "",
        subheadCode: "",
        posDisplay: "",
        type: "Non_Tax",
      });
      setEditingId(null);
    } catch (err) {
      console.error("Error saving subhead:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (s) => {
    setForm({
      head: s.head,
      headId: s.headId,
      govtPercentage: s.govtPercentage,
      agentPercentage: s.agentPercentage,
      agencyPercentage: s.agencyPercentage,
      baseAmount: s.baseAmount,
      amount: s.amount,
      subhead: s.subhead,
      subheadCode: s.subheadCode,
      posDisplay: s.posDisplay,
      type: s.type,
    });
    setEditingId(s.id);
  };

  return (
    <div className="max-w-full mx-auto p-6 font-sans text-gray-800">
      <h2 className="text-3xl font-bold text-blue-800 mb-6 text-center">
        Subheads Manager
      </h2>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-lg shadow-md mb-10"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            name="head"
            value={form.head}
            onChange={handleChange}
            placeholder="Head"
            required
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            name="headId"
            value={form.headId}
            onChange={handleChange}
            placeholder="Head ID"
            required
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          />

          {/* Tooltip fields */}
          {[
            {
              name: "govtPercentage",
              label: "Govt Percentage",
              tip: "Government share percentage (defaults to 0)",
            },
            {
              name: "agentPercentage",
              label: "Agent Percentage",
              tip: "Agent share percentage (defaults to 0)",
            },
            {
              name: "agencyPercentage",
              label: "Agency Percentage",
              tip: "Agency share percentage (defaults to 0)",
            },
            {
              name: "baseAmount",
              label: "Base Amount",
              tip: "Base amount before additional fees (defaults to 0)",
            },
            {
              name: "amount",
              label: "Amount",
              tip: "Total payable amount (can be adjusted)",
            },
          ].map((f) => (
            <div
              key={f.name}
              className="relative flex items-center"
              onMouseEnter={() => setTooltip(f.tip)}
              onMouseLeave={() => setTooltip("")}
            >
              <input
                type="number"
                name={f.name}
                value={form[f.name]}
                onChange={handleChange}
                placeholder={f.label}
                className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
              />
              <span className="ml-2 text-gray-400 cursor-help">ℹ️</span>
            </div>
          ))}

          <input
            type="text"
            name="subhead"
            value={form.subhead}
            onChange={handleChange}
            placeholder="Subhead"
            required
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            name="subheadCode"
            value={form.subheadCode}
            onChange={handleChange}
            placeholder="Subhead Code"
            required
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            name="posDisplay"
            value={form.posDisplay}
            onChange={handleChange}
            placeholder="POS Display Name"
            required
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          />
          <select
            name="type"
            value={form.type}
            onChange={handleChange}
            className="border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-blue-500"
          >
            <option value="Non_Tax">Non_Tax</option>
            <option value="Tax">Tax</option>
          </select>
        </div>

        {tooltip && (
          <small className="text-gray-500 mt-2 block">{tooltip}</small>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-4 bg-blue-600 text-white font-bold py-2 px-6 rounded hover:bg-blue-700 transition"
        >
          {editingId ? "Update Subhead" : "Create Subhead"}
        </button>
      </form>

      {/* Table */}
      <h2 className="text-xl font-semibold mb-4 border-b-2 border-blue-800 pb-2">
        All Subheads
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border border-gray-200 whitespace-nowrap bg-white rounded-lg shadow-sm">
          <thead className="bg-blue-800 text-white">
            <tr>
              <th className="px-4 py-2 text-left">Subhead</th>
              <th className="px-4 py-2 text-left">Subhead Code</th>
              <th className="px-4 py-2 text-left">Head</th>
              <th className="px-4 py-2 text-left">Amount</th>
              <th className="px-4 py-2 text-left">POS Display</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {subheads.map((s, i) => (
              <tr
                key={s.id}
                className={
                  i % 2 === 0 ? "bg-white" : "bg-gray-50 hover:bg-blue-50"
                }
              >
                <td className="px-4 py-2">{s.subhead}</td>
                <td className="px-4 py-2">{s.subheadCode}</td>
                <td className="px-4 py-2">{s.head}</td>
                <td className="px-4 py-2">{s.amount}</td>
                <td className="px-4 py-2">{s.posDisplay}</td>
                <td className="px-4 py-2">{s.type}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => handleEdit(s)}
                    className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500 transition"
                  >
                    ✏️ Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubheadsManager;
